import { IconAlarm, IconChevronDown, IconChevronUp, IconMinus, IconPlus } from '@tabler/icons-react';
import { cx } from '@wapharma/design-system';
import styles from './StockAlertsVisualFields.module.css';

const LOT_VISUAL_CAP = 12;
const NEAR_PRESETS = [7, 14, 30, 60, 90] as const;

function clampInt(n: number, min: number, max?: number): number {
  if (!Number.isFinite(n)) return min;
  const v = Math.trunc(n);
  if (max != null) return Math.min(max, Math.max(min, v));
  return Math.max(min, v);
}

export function StockLowThresholdVisual({
  value,
  onChange,
  disabled,
  hint,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  hint?: string;
}) {
  const qty = clampInt(value, 0);
  const filled = Math.min(qty, LOT_VISUAL_CAP);
  const overflow = qty > LOT_VISUAL_CAP ? qty - LOT_VISUAL_CAP : 0;

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <span className={styles.label}>Stock bas</span>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </div>
      <div className={styles.lotStage}>
        <div className={styles.lotGrid} role="group" aria-label={`Seuil stock bas : ${qty}`}>
          {Array.from({ length: LOT_VISUAL_CAP }, (_, i) => {
            const n = i + 1;
            const isFilled = n <= filled;
            return (
              <button
                key={n}
                type="button"
                className={cx(styles.lotBox, isFilled && styles.lotBoxFilled)}
                disabled={disabled}
                aria-label={`${n}`}
                aria-pressed={qty === n}
                onClick={() => onChange(n)}
              />
            );
          })}
          {overflow > 0 ? (
            <span className={styles.lotOverflow} aria-hidden>
              +{overflow}
            </span>
          ) : null}
        </div>
        <div className={styles.stepperRow}>
          <button
            type="button"
            className={styles.stepperBtn}
            disabled={disabled || qty <= 0}
            aria-label="Diminuer"
            onClick={() => onChange(Math.max(0, qty - 1))}
          >
            <IconMinus size={15} stroke={2} aria-hidden />
          </button>
          <input
            className={styles.valueInput}
            type="number"
            min={0}
            inputMode="numeric"
            value={String(qty)}
            disabled={disabled}
            aria-label="Seuil stock bas"
            onChange={(e) => {
              const raw = e.currentTarget.value.trim();
              if (raw === '') {
                onChange(0);
                return;
              }
              const n = Number.parseInt(raw, 10);
              if (Number.isFinite(n)) onChange(clampInt(n, 0));
            }}
          />
          <button
            type="button"
            className={styles.stepperBtn}
            disabled={disabled}
            aria-label="Augmenter"
            onClick={() => onChange(qty + 1)}
          >
            <IconPlus size={15} stroke={2} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

export function NearExpiryDaysVisual({
  value,
  onChange,
  disabled,
  hint,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  hint?: string;
}) {
  const days = clampInt(value, 1);

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <span className={styles.label}>Péremption</span>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </div>
      <div className={styles.alarmCard}>
        <IconAlarm size={20} stroke={1.6} className={styles.alarmIcon} aria-hidden />
        <div className={styles.alarmDrum} role="group" aria-label={`Alerte ${days} jours avant péremption`}>
          <button
            type="button"
            className={styles.alarmChevron}
            disabled={disabled}
            aria-label="Augmenter"
            onClick={() => onChange(days + 1)}
          >
            <IconChevronUp size={18} stroke={2} aria-hidden />
          </button>
          <div className={styles.alarmValueRow}>
            <input
              className={styles.alarmValue}
              type="number"
              min={1}
              inputMode="numeric"
              value={String(days)}
              disabled={disabled}
              aria-label="Jours avant péremption"
              onChange={(e) => {
                const raw = e.currentTarget.value.trim();
                if (raw === '') return;
                const n = Number.parseInt(raw, 10);
                if (Number.isFinite(n)) onChange(clampInt(n, 1));
              }}
            />
            <span className={styles.alarmSuffix} aria-hidden>
              j
            </span>
          </div>
          <button
            type="button"
            className={styles.alarmChevron}
            disabled={disabled || days <= 1}
            aria-label="Diminuer"
            onClick={() => onChange(Math.max(1, days - 1))}
          >
            <IconChevronDown size={18} stroke={2} aria-hidden />
          </button>
        </div>
        <div className={styles.presetRow}>
          {NEAR_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={cx(styles.preset, days === p && styles.presetActive)}
              disabled={disabled}
              aria-label={`${p} jours`}
              aria-pressed={days === p}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
